import { Box, Container } from '@mui/material';
import { Link, useParams } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Footer } from '../../components/footers/Footer';
import { HomeHeader } from '../../components/headers/HomeHeader';
import { PageMain } from '../../components/seo/PageMain';
import { PageSeoHelmet } from '../../components/seo/PageSeoHelmet';
import { ArticlePageHeader } from './components/ArticlePageHeader';
import { ArticleSections } from './components/ArticleSections';
import { ArticleToc } from './components/ArticleToc';
import { buildArticlePage } from './repository';
import { KC_COLORS } from './theme';
import { NotFound } from '../notFound/NotFound';
import './knowledgeCenter.css';

gsap.registerPlugin(useGSAP, ScrollTrigger);

export function KnowledgeCenterArticle({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.articleSlug;
  const pageScope = useRef(null);
  const [page, setPage] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    setPage(null);

    buildArticlePage(slug).then((nextPage) => {
      if (cancelled) return;
      if (!nextPage) {
        setStatus('missing');
        return;
      }
      setPage(nextPage);
      setStatus('ready');
    });

    return () => {
      cancelled = true;
    };
  }, [slug]);

  const scrollToSection = useCallback((sectionId) => {
    const el = document.getElementById(sectionId);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  useGSAP(
    () => {
      if (!page) return undefined;

      const intro = gsap.timeline({ defaults: { ease: 'power2.out' } });
      intro
        .fromTo(
          '.kc-article-breadcrumb',
          { y: 14, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.4 }
        )
        .fromTo(
          '.kc-article-title',
          { y: 26, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.55 },
          '-=0.15'
        )
        .fromTo(
          '.kc-article-summary',
          { y: 16, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.45 },
          '-=0.25'
        )
        .fromTo(
          '.kc-article-meta',
          { y: 12, opacity: 0 },
          { y: 0, opacity: 1, duration: 0.4 },
          '-=0.2'
        );

      ScrollTrigger.create({
        trigger: '.kc-article-body',
        start: 'top 85%',
        once: true,
        onEnter: () => {
          gsap.fromTo(
            '.kc-article-section',
            { y: 22, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.45,
              stagger: 0.06,
              ease: 'power2.out',
            }
          );
          gsap.fromTo(
            '.kc-article-toc',
            { y: 20, opacity: 0 },
            { y: 0, opacity: 1, duration: 0.5, ease: 'power2.out', delay: 0.1 }
          );
        },
      });

      return undefined;
    },
    { scope: pageScope, dependencies: [slug, page] }
  );

  if (status === 'missing') {
    return <NotFound />;
  }

  if (status === 'loading' || !page) {
    return (
      <div className="kc-ds w-full overflow-x-hidden bg-white min-h-screen">
        <HomeHeader headerColor="#0D131E" textColor="#ffffff" />
        <PageMain className="knowledge-center-article-page">
          <Box className="w-full bg-white pb-12 md:pb-16">
            <Container maxWidth="lg">
              <div className="pt-28 md:pt-32 pb-16">
                <p className="kc-type-body" style={{ color: KC_COLORS.gray600 }}>
                  Loading article…
                </p>
              </div>
            </Container>
          </Box>
        </PageMain>
        <Footer />
      </div>
    );
  }

  const relatedArticles = page.related || [];
  const bodySections = page.sections;
  const faqIndex = bodySections.findIndex((s) => s.variant === 'faq');
  const splitIndex = faqIndex === -1 ? bodySections.length : faqIndex;
  const narrowSections = bodySections.slice(0, splitIndex);
  const fullWidthSections = bodySections.slice(splitIndex);

  return (
    <>
      <PageSeoHelmet {...page.seo} />
      <div className="kc-ds w-full overflow-x-hidden bg-white">
        <HomeHeader headerColor="#0D131E" textColor="#ffffff" />
        <PageMain ref={pageScope} className="knowledge-center-article-page">
          <Box className="w-full bg-white pb-12 md:pb-16">
            <Container maxWidth="lg">
              <ArticlePageHeader article={page} />

              <hr
                className="mt-8 md:mt-10 mb-8 md:mb-10 border-0 border-t"
                style={{ borderColor: KC_COLORS.border }}
              />

              <div className="kc-article-body text-left">
                {narrowSections.length > 0 ? (
                  <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_280px] gap-8 lg:gap-10 items-start">
                    <div className="min-w-0 text-left">
                      <ArticleSections
                        sections={narrowSections}
                        openFirstOnMobile
                      />
                    </div>
                    <ArticleToc
                      sections={bodySections}
                      onNavigate={scrollToSection}
                    />
                  </div>
                ) : null}

                {fullWidthSections.length > 0 ? (
                  <div
                    className={`w-full min-w-0 text-left ${
                      narrowSections.length > 0 ? 'mt-8 md:mt-10' : ''
                    }`}
                  >
                    <ArticleSections sections={fullWidthSections} />
                  </div>
                ) : null}

                {relatedArticles.length > 0 ? (
                  <div className="kc-related-reading mt-10 md:mt-12">
                    <h2
                      className="kc-related-reading-title kc-type-h2 m-0"
                      style={{ color: KC_COLORS.primaryNavy }}
                    >
                      Related Reading
                    </h2>

                    <ul className="grid grid-cols-1 md:grid-cols-2 gap-3.5 list-none p-0 m-0 items-stretch">
                      {relatedArticles.map((related) => (
                        <li key={related.id} className="h-full">
                          <Link
                            to={related.path}
                            className="kc-related-card group h-full no-underline"
                          >
                            <span className="kc-related-card-id">{related.id}</span>
                            <span className="kc-related-card-heading">
                              <span>{related.title}</span>
                            </span>
                            <span className="kc-related-card-time">
                              {related.readTime || 'Read'}
                            </span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                {page.cta ? (
                  <div className="kc-article-cta mt-10 md:mt-12">
                    <div className="kc-article-cta-inner">
                      <p className="kc-article-cta-eyebrow">Next step</p>
                      <h2
                        className="kc-type-h3 m-0"
                        style={{ color: KC_COLORS.primaryNavy }}
                      >
                        {page.cta.label}
                      </h2>
                      <p
                        className="kc-type-body mt-3 m-0 max-w-3xl"
                        style={{ color: KC_COLORS.navy700 }}
                      >
                        {page.cta.text}
                      </p>
                      <Link
                        to={page.cta.path}
                        className="kc-article-cta-button"
                      >
                        {page.cta.label}
                        <span aria-hidden="true">→</span>
                      </Link>
                    </div>
                  </div>
                ) : null}
              </div>
            </Container>
          </Box>
        </PageMain>
        <Footer />
      </div>
    </>
  );
}

export function KnowledgeCenterArticlePage() {
  return <KnowledgeCenterArticle />;
}
